import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiErr, get, type Env } from './api';
import { reportClientError } from './sentry';

export interface Paged { total: number; offset: number; next: number | null }

export interface PagedList<T, X> {
  rows: T[];
  total: number | null;
  data: X | null;
  error: ApiErr | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  at: number | null;
  ageS: number;
  loadMore: () => void;
  reload: () => void;
}

const PAGE = 50;
const REFRESH_MAX = 200;

export function pagePath(path: string, offset: number, limit: number): string {
  const [base, query = ''] = path.split('?');
  const p = new URLSearchParams(query);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  return `${base}?${p.toString()}`;
}

export function qs(o: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== '' && v !== false && v !== null && v !== undefined) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function usePagedList<T, X extends Paged = Paged>(env: Env, path: string | null, rowsKey: keyof X & string, everyS: number, paused: boolean, rowKey: (r: T) => string = (r) => (r as { id?: string }).id ?? JSON.stringify(r)): PagedList<T, X> {
  const [rows, setRows] = useState<T[]>([]);
  const [data, setData] = useState<X | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [error, setError] = useState<ApiErr | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [loadingMore, setLoadingMore] = useState(false);
  const [at, setAt] = useState<number | null>(null);
  const [, setTick] = useState(0);
  const gen = useRef(0);
  const rowsRef = useRef<T[]>([]);
  rowsRef.current = rows;
  const keyRef = useRef(rowKey);
  keyRef.current = rowKey;

  const refresh = useCallback(async (fresh: boolean) => {
    if (!path) return;
    const g = ++gen.current;
    const n = fresh ? PAGE : Math.min(REFRESH_MAX, Math.max(PAGE, rowsRef.current.length));
    try {
      const d = await get<X>(env, pagePath(path, 0, n));
      if (g !== gen.current) return;
      const head = (d[rowsKey] as unknown as T[]) ?? [];
      const seen = new Set(head.map(keyRef.current));
      const tail = fresh ? [] : rowsRef.current.slice(n).filter((r) => !seen.has(keyRef.current(r)));
      setRows([...head, ...tail]);
      setData(d);
      setTotal(d.total ?? null);
      setNext(tail.length ? (d.total > n + tail.length ? n + tail.length : null) : d.next ?? null);
      setError(null);
      setAt(Date.now());
    } catch (e) {
      if (g !== gen.current) return;
      reportClientError(e);
      setError(e as ApiErr);
    } finally {
      if (g === gen.current) setLoading(false);
    }
  }, [env, path, rowsKey]);

  useEffect(() => {
    setRows([]);
    setData(null);
    setTotal(null);
    setNext(null);
    setError(null);
    setAt(null);
    setLoading(!!path);
    void refresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, path]);

  useEffect(() => {
    if (!path || everyS <= 0 || paused) return;
    const id = setInterval(() => { if (document.visibilityState === 'visible') void refresh(false); }, everyS * 1000);
    return () => clearInterval(id);
  }, [path, everyS, paused, refresh]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const loadMore = useCallback(async () => {
    if (!path || next === null || loadingMore) return;
    const g = gen.current;
    setLoadingMore(true);
    try {
      const d = await get<X>(env, pagePath(path, next, PAGE));
      if (g !== gen.current) return;
      const more = (d[rowsKey] as unknown as T[]) ?? [];
      const seen = new Set(rowsRef.current.map(keyRef.current));
      setRows((r) => [...r, ...more.filter((x) => !seen.has(keyRef.current(x)))]);
      setTotal(d.total ?? null);
      setNext(d.next ?? null);
    } catch (e) {
      reportClientError(e);
      setError(e as ApiErr);
    } finally {
      setLoadingMore(false);
    }
  }, [env, path, next, loadingMore, rowsKey]);

  return {
    rows, total, data, error, loading, loadingMore, hasMore: next !== null, at,
    ageS: at ? Math.floor((Date.now() - at) / 1000) : 0,
    loadMore: () => void loadMore(),
    reload: () => void refresh(false),
  };
}

export function countOf(l: { rows: unknown[]; total: number | null }): string | undefined {
  if (l.total === null) return undefined;
  return l.rows.length < l.total ? `${l.rows.length.toLocaleString()} of ${l.total.toLocaleString()}` : l.total.toLocaleString();
}

export function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}
